/**
 * engineering_panel.ts — what a pilot SEES of engineering (space campaign slice G, 2026-09-30).
 *
 *   THE HUD STRIP (`#space-eng-strip`, the right column under the target readout): one line per group that is not healthy — cause and
 *   action, a glyph and words ("⚠ Engine degraded (42) — acceleration reduced — reroute (3) or request repair (')"), the
 *   running patch with its progress, who routes power on a crewed hull, an invite waiting. Never colour alone.
 *   THE CONFIRMED-HIT MARK (`#space-eng-hit`, under the reticle): "✓ ON THE RECORD" for 700 ms when the server's record
 *   moved for my shot — distinct from the local (predicted) spark.
 *   THE ENGINEER OVERLAY (window `engineering`, key `spaceEngineering`): the two groups with their state WORD and effect,
 *   the hull, power and its writer, the patch buttons (disabled WITH the reason printed), the job, the kits, the crew
 *   (pilot / gunner / engineer with epochs; invite, revoke, accept, decline, leave), the safe-dock restore, the receipts.
 * Tokens only (THE TOKEN LAW); every control is a labelled button with a glyph; it fits 720p at 200 % (it scrolls).
 */
import { makeWindow, type UiWindow } from './ui_window.js';
import { softRender } from './soft_render.js';
import { keyHint } from './keybinds.js';
import { shipEngineering, type EngView } from './ship_engineering_client.js';
import { FIELD_CAP, GROUP_MAX, ENGINE_LIMP, WEAPONS_DEGRADED_CD, ENGINE_DEGRADED_ACCEL, groupState, type CrewRole } from '@shared/ship_engineering.js';

const STATE_WORD: Record<string, string> = { healthy: 'HEALTHY', degraded: 'DEGRADED', disabled: 'DISABLED' };
const STATE_TONE: Record<string, string> = { healthy: 'var(--ax-good)', degraded: 'var(--ax-warn)', disabled: 'var(--ax-bad)' };

function el<K extends keyof HTMLElementTagNameMap>(tag: K, css = '', text = ''): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (css) e.style.cssText = css;
  if (text) e.textContent = text;
  return e;
}
function btn(label: string, act: string, arg = '', disabledWhy: string | null = null, primary = false): HTMLButtonElement {
  const b = el('button', 'margin:2px 4px 2px 0;font:600 12px var(--ax-font);cursor:pointer', label);
  b.type = 'button';
  b.className = primary ? 'ax-btn is-primary' : 'ax-btn';
  b.dataset.act = act;
  if (arg) b.dataset.arg = arg;
  b.dataset.k = `${act}:${arg}`;
  if (disabledWhy) { b.disabled = true; b.title = disabledWhy; }
  return b;
}

class EngineeringPanel {
  private strip: HTMLDivElement | null = null;
  private hit: HTMLDivElement | null = null;
  private win: UiWindow | null = null;
  private lastRender = 0;
  private watchdog = 0;

  /** Called every frame by the sector (through the controller's tick). */
  frame(nowMs: number): void {
    this.ensureHud();
    if (nowMs - this.lastRender < 250) return;
    this.lastRender = nowMs;
    const v = shipEngineering.view();
    this.renderStrip(v, nowMs);
    if (this.win?.isOpen()) this.renderWindow(v);
  }

  private ensureHud(): void {
    if (!this.strip) {
      this.strip = el('div',
        // The right column UNDER the target readout (top 14 px, ~70 px tall): the bottom-left is the ship status panel and
        // the chat, the right column's lower half the Pulse gauges (bottom 200 px), the help button and the radar.
        'position:fixed;right:16px;top:120px;z-index:34;max-width:min(440px, 40vw);pointer-events:none;display:none;text-align:right;' +
        'background:color-mix(in srgb, var(--ax-bg) 80%, transparent);border:1px solid var(--ax-line);border-radius:var(--ax-win-radius);' +
        'padding:6px 10px;font:600 12px var(--ax-font);color:var(--ax-text);line-height:1.35');
      this.strip.id = 'space-eng-strip';
      this.strip.setAttribute('role', 'status');
      this.strip.setAttribute('aria-live', 'polite');
      document.body.appendChild(this.strip);
    }
    if (!this.hit) {
      this.hit = el('div',
        'position:fixed;left:50%;top:calc(50% - 46px);transform:translateX(-50%);z-index:35;pointer-events:none;display:none;' +
        'font:700 11px var(--ax-mono);letter-spacing:.6px;color:var(--ax-good);text-shadow:0 1px 3px var(--ax-bg)', '✓ HIT — ON THE RECORD');
      this.hit.id = 'space-eng-hit';
      document.body.appendChild(this.hit);
    }
    if (!this.watchdog) {
      // The sector stops calling frame() when it deactivates — the strip must not outlive it.
      this.watchdog = window.setInterval(() => {
        if (shipEngineering.stale(performance.now())) {
          if (this.strip) this.strip.style.display = 'none';
          if (this.hit) this.hit.style.display = 'none';
          if (this.win?.isOpen()) this.win.setOpen(false);
        }
      }, 500);
    }
  }

  private renderStrip(v: EngView, nowMs: number): void {
    const s = this.strip!;
    const compact = window.innerHeight < 520 || window.innerWidth < 900;
    // At 720p × 200 % (640×360 CSS px) the right column is the Pulse gauges (bottom 200 px) and the radar (16–164 px)
    // with ~36 px between them: the compact strip is ONE line there — glyph, group word, value, and the key for the
    // full sentences (the overlay). Never colour alone: the glyph and the word carry the state.
    s.style.maxWidth = compact ? 'min(420px, 60vw)' : 'min(440px, 40vw)';
    s.style.fontSize = compact ? '11px' : '12px';
    s.style.top = compact ? 'auto' : '120px';
    s.style.bottom = compact ? '168px' : '';
    s.style.whiteSpace = compact ? 'nowrap' : '';
    s.style.overflow = compact ? 'hidden' : '';
    s.style.textOverflow = compact ? 'ellipsis' : '';
    s.style.padding = compact ? '3px 8px' : '6px 10px';
    const lines: Array<{ text: string; tone: string }> = [];
    const patchK = keyHint('spacePatch');
    if (v.on && v.role !== 'gunner') {
      for (const l of v.lines) {
        if (l.state === 'healthy') continue;
        const name = l.group === 'engine' ? 'Engine' : 'Weapons';
        lines.push({ text: compact ? `${l.glyph} ${name} ${v.health[l.group]}` : `${l.glyph} ${l.text}`, tone: STATE_TONE[l.state] });
      }
      if (v.job) {
        const pct = Math.round(v.job.pct * 100);
        lines.push({ text: compact ? `⚒ ${pct} %` : `⚒ ${v.job.mine ? 'Patching' : `${v.job.actorName} patches`} the ${v.job.target} — ${pct} %${v.job.mine ? ` (${patchK} cancels; a hit stops it)` : ''}`, tone: 'var(--ax-info)' });
      }
      if (v.power && (v.role === 'engineer' || v.power.role === 'engineer' || v.leases.some((x) => x.role === 'engineer' && x.state === 'active')))
        lines.push({ text: compact ? `⚡ ${v.power.preset.toUpperCase()}` : `⚡ Power: ${v.power.preset.toUpperCase()} — set by ${v.power.by} (${v.power.role})`, tone: 'var(--ax-accent)' });
      if (v.role === 'engineer' && v.myLease) lines.push({ text: compact ? '⚙ station' : `⚙ Engineer station — ${v.captainName}'s Sovereign · ${keyHint('spaceEngineering')} panel · ${patchK} patch · 1–4 route power`, tone: 'var(--ax-soft)' });
    }
    if (v.role === 'gunner' && v.myLease) lines.push({ text: compact ? '◎ gunner' : `◎ Gunner — ${v.captainName}'s Sovereign (epoch ${v.myLease.epoch})`, tone: 'var(--ax-soft)' });
    for (const inv of v.invitesToMe) lines.push({ text: compact ? `✉ ${inv.role} invite` : `✉ ${inv.captainName} invites you as ${inv.role.toUpperCase()} (${inv.expiresInS} s) — press ${keyHint('spaceEngineering')} to answer`, tone: 'var(--ax-accent)' });
    if (compact && lines.length) lines.push({ text: `${keyHint('spaceEngineering')} details`, tone: 'var(--ax-muted)' });
    softRender(s, (sc) => {
      lines.forEach((l, i) => {
        if (compact) { if (i) sc.appendChild(el('span', 'color:var(--ax-muted)', ' · ')); sc.appendChild(el('span', `color:${l.tone}`, l.text)); }
        else sc.appendChild(el('div', `color:${l.tone}`, l.text));
      });
    });
    s.style.display = lines.length ? 'block' : 'none';
    this.hit!.style.display = v.confirmedAt > 0 && performance.now() - v.confirmedAt < 700 ? 'block' : 'none';
    void nowMs;
  }

  // ── The overlay ──────────────────────────────────────────────────────────────────────────────────────────────────
  toggle(): void {
    if (!this.win) {
      this.win = makeWindow({ id: 'engineering', title: 'Engineering', icon: 'gear', width: Math.min(470, window.innerWidth - 24), height: Math.min(600, window.innerHeight - 96), minWidth: 300, minHeight: 220, onClose: () => this.win?.setOpen(false) });
      this.win.body.style.cssText += ';overflow:auto;padding:10px 12px;font:13px var(--ax-font);color:var(--ax-text)';
      this.win.body.addEventListener('click', (e) => this.onClick(e));
    }
    this.win.setOpen(!this.win.isOpen());
    if (this.win.isOpen()) this.renderWindow(shipEngineering.view());
  }
  isOpen(): boolean { return !!this.win?.isOpen(); }

  private onClick(e: Event): void {
    const b = (e.target as HTMLElement).closest('button[data-act]') as HTMLButtonElement | null;
    if (!b || b.disabled) return;
    const act = b.dataset.act!, arg = b.dataset.arg ?? '';
    const budget = Number((this.win!.body.querySelector('select[data-k="budget"]') as HTMLSelectElement | null)?.value ?? 0);
    switch (act) {
      case 'patch': shipEngineering.patch(arg); break;
      case 'cancel': shipEngineering.cancel(); break;
      case 'dock': shipEngineering.restoreAtDock(); break;
      case 'invite-gunner': shipEngineering.invite(arg, 'gunner', 0); break;
      case 'invite-engineer': shipEngineering.invite(arg, 'engineer', budget); break;
      case 'revoke': shipEngineering.revoke(BigInt(arg)); break;
      case 'accept': shipEngineering.accept(BigInt(arg)); break;
      case 'decline': shipEngineering.decline(BigInt(arg)); break;
      case 'leave': shipEngineering.release(); break;
      default: break;
    }
    this.lastRender = 0;
  }

  private renderWindow(v: EngView): void {
    const body = this.win!.body;
    softRender(body, (sc) => {
      const head = (t: string): void => { const h = el('div', 'margin:10px 0 4px;font:700 11px var(--ax-mono);letter-spacing:1px;color:var(--ax-muted)', t); h.className = 'ax-head'; sc.appendChild(h); };
      const note = (t: string, tone = 'var(--ax-soft)'): void => { sc.appendChild(el('div', `margin:2px 0;color:${tone};font-size:12px`, t)); };
      if (!v.on) { note('Ship engineering is off on this shard — no subsystem damage, no field patch, and the turret seat is open to any pilot (crew_engineering_v1 = false).', 'var(--ax-warn)'); return; }
      const who = v.role === 'engineer' ? `Engineer station — ${v.captainName}'s ${v.shipName || 'Sovereign'}` : v.role === 'gunner' ? `Gunner — ${v.captainName}'s Sovereign` : `${v.shipName || 'Your hull'} — you fly it`;
      sc.appendChild(el('div', 'font:700 14px var(--ax-display);color:var(--ax-hi);margin-bottom:2px', `⚙ ${who}`));
      if (v.role === 'gunner') { note('The turret is your station: aim and fire your mount. Repairs and power are the engineer\'s and the pilot\'s.'); }

      head('SUBSYSTEMS');
      for (const l of v.lines) {
        const hp = v.health[l.group], st = groupState(hp);
        const row = el('div', 'display:flex;align-items:center;gap:8px;margin:3px 0');
        row.appendChild(el('span', `width:18px;text-align:center;color:${STATE_TONE[st]}`, l.glyph));
        row.appendChild(el('span', 'width:70px;font-weight:700', l.group === 'engine' ? 'Engine' : 'Weapons'));
        const bar = el('span', 'flex:1;height:8px;border:1px solid var(--ax-line);border-radius:var(--ax-radius);overflow:hidden;background:var(--ax-bg2);position:relative');
        const fill = el('span', `display:block;height:100%;width:${hp}%;background:${STATE_TONE[st]}`);
        const cap = el('span', `position:absolute;left:${FIELD_CAP}%;top:-2px;bottom:-2px;width:1px;background:var(--ax-muted)`);
        cap.title = `the field cap (${FIELD_CAP})`;
        bar.append(fill, cap);
        row.appendChild(bar);
        row.appendChild(el('span', `width:118px;font:600 12px var(--ax-mono);color:${STATE_TONE[st]}`, `${hp}/${GROUP_MAX} ${STATE_WORD[st]}`));
        sc.appendChild(row);
        const eff = l.group === 'engine'
          ? (st === 'healthy' ? 'full thrust and response' : st === 'degraded' ? `acceleration ×${ENGINE_DEGRADED_ACCEL} (top speed holds)` : `limping at ${Math.round(ENGINE_LIMP * 100)} % thrust, steering holds, no boost or overcruise — the hyperdrive still jumps once you are out of fire`)
          : (st === 'healthy' ? 'the cannons at full rate' : st === 'degraded' ? `the cannons cycle ×${WEAPONS_DEGRADED_CD} slower` : 'the cannons cannot fire — the missile rack still can');
        note(`   ${eff}${(l.group === 'engine' ? v.judged.engine : v.judged.weapons) > 0 ? ` · ${l.group === 'engine' ? v.judged.engine : v.judged.weapons} of the damage was judged combat damage` : ''}`);
      }
      if (v.lastCause) note(`Last: ${v.lastCause}`, 'var(--ax-muted)');
      note(v.role === 'pilot' ? 'Hull: on your own instruments (the ship status panel).' : 'Hull: on the captain\'s instruments (not shared) — a hull patch lands there.', 'var(--ax-muted)');

      if (v.role !== 'gunner') {
        head('FIELD PATCH — one repair kit, 8 exposed seconds');
        if (v.job) {
          const jr = el('div', 'margin:4px 0');
          jr.appendChild(el('div', 'font-weight:600', `⚒ ${v.job.mine ? 'You are patching' : `${v.job.actorName} is patching`} the ${v.job.target} — ${Math.round(v.job.pct * 100)} %`));
          const pb = el('div', 'height:6px;border:1px solid var(--ax-line);border-radius:var(--ax-radius);overflow:hidden;background:var(--ax-bg2);margin:3px 0');
          pb.appendChild(el('div', `height:100%;width:${Math.round(v.job.pct * 100)}%;background:var(--ax-info)`));
          jr.appendChild(pb);
          if (v.job.mine) jr.appendChild(btn(`✕ Cancel (${keyHint('spacePatch')})`, 'cancel'));
          sc.appendChild(jr);
        } else {
          const bar = el('div', 'display:flex;flex-wrap:wrap');
          bar.appendChild(btn('⚒ Patch engine (+20, ≤ 60)', 'patch', 'engine', v.patchWhy.engine ?? null));
          bar.appendChild(btn('⚒ Patch weapons (+20, ≤ 60)', 'patch', 'weapons', v.patchWhy.weapons ?? null));
          bar.appendChild(btn('⚒ Patch hull (+10)', 'patch', 'hull', v.patchWhy.hull ?? null));
          sc.appendChild(bar);
          for (const t of ['engine', 'weapons', 'hull']) if (v.patchWhy[t]) note(`   ${t}: ${v.patchWhy[t]}`, 'var(--ax-muted)');
        }
        note(`Kits: ${v.kits.own} of your own${v.role === 'engineer' ? ` · the captain's: ${v.kits.captain} (you may draw ${v.kits.budgetLeft})` : ''} — a kit is 2 metal ingots + 1 field tool (the Crafting Guide, F10). Shortcut: ${keyHint('spacePatch')} patches the worst group.`);
        if (v.droid && v.role === 'pilot') note(`Droid field stock: ${v.droid.stock}/${v.droid.max} hull — it nurses and patches from this, and restocks at a safe dock.`);
        if (v.role === 'pilot' && v.needsDock) {
          sc.appendChild(btn('⚓ Restore at the dock (free — safe-dock subsidy)', 'dock', '', v.safeDock ? null : 'Pull up to a station (its dock range) or your own depot'));
          note(v.safeDock ? '   A dock restores both groups to 100 — the field patch stops at 60.' : '   Docking (or pulling alongside a station / your depot) restores both groups to 100.', 'var(--ax-muted)');
        }
      }

      head('POWER');
      note(v.power ? `⚡ ${v.power.preset.toUpperCase()} — set by ${v.power.by} (${v.power.role})` : `⚡ ${v.role === 'pilot' ? 'your own routing' : 'the captain\'s routing'} — ${keyHint('spacePowerWeapons')} weapons · ${keyHint('spacePowerShields')} shields · ${keyHint('spacePowerEngines')} engines · ${keyHint('spacePowerBalanced')} balanced`);
      if (v.role === 'engineer') note('   Your 1–4 route the captain\'s power (one writer at a time — the pilot can take it back).', 'var(--ax-muted)');

      head('CREW');
      if (v.role === 'pilot') {
        note('◉ Pilot — you (the helm, travel, the fit and the hull are yours alone).');
        for (const r of ['gunner', 'engineer'] as CrewRole[]) {
          const l = v.leases.find((x) => x.role === r);
          const row = el('div', 'display:flex;align-items:center;flex-wrap:wrap;gap:6px;margin:2px 0');
          row.appendChild(el('span', 'min-width:84px;font-weight:700', r === 'gunner' ? '◎ Gunner' : '⚙ Engineer'));
          if (l) {
            row.appendChild(el('span', '', l.state === 'active' ? `${l.holderName} · epoch ${l.epoch}${r === 'engineer' ? ` · your kits ${l.kitsUsed}/${l.kitBudget}` : ''}` : `${l.holderName} — invited`));
            row.appendChild(btn(l.state === 'active' ? '✕ Revoke' : '✕ Withdraw', 'revoke', String(l.leaseId)));
          } else row.appendChild(el('span', 'color:var(--ax-muted)', v.canInvite ? 'vacant' : 'a Sovereign carries crew stations'));
          sc.appendChild(row);
        }
        if (v.canInvite) {
          const sel = el('select', 'margin-left:6px');
          sel.className = 'ax-input';
          sel.dataset.k = 'budget';
          for (let k = 0; k <= 5; k++) { const o = el('option', '', String(k)); o.value = String(k); sel.appendChild(o); }
          const lb = el('div', 'margin:6px 0 2px;font-size:12px', 'Engineer\'s budget of YOUR kits:');
          lb.appendChild(sel);
          sc.appendChild(lb);
          if (!v.nearby.length) note('No pilot within 3 km to invite.', 'var(--ax-muted)');
          for (const p of v.nearby.slice(0, 8)) {
            const row = el('div', 'display:flex;align-items:center;flex-wrap:wrap;gap:4px;margin:2px 0');
            row.appendChild(el('span', 'min-width:120px', `${p.name} · ${Math.round(p.d)} m`));
            row.appendChild(btn('◎ Invite gunner', 'invite-gunner', p.hex));
            row.appendChild(btn('⚙ Invite engineer', 'invite-engineer', p.hex));
            sc.appendChild(row);
          }
        }
      } else if (v.myLease) {
        note(`You hold the ${v.myLease.role.toUpperCase()} station (epoch ${v.myLease.epoch}) on ${v.myLease.captainName}'s Sovereign${v.myLease.role === 'engineer' ? ` — the captain's kits: ${v.myLease.kitsUsed}/${v.myLease.kitBudget} used` : ''}.`);
        sc.appendChild(btn(`⇦ Leave the station (${keyHint('spaceTurret')})`, 'leave'));
      }
      for (const inv of v.invitesToMe) {
        const row = el('div', 'display:flex;align-items:center;flex-wrap:wrap;gap:4px;margin:4px 0');
        row.appendChild(el('span', 'font-weight:600', `✉ ${inv.captainName} invites you as ${inv.role.toUpperCase()} (${inv.expiresInS} s)`));
        row.appendChild(btn('✓ Accept', 'accept', String(inv.leaseId), null, true));
        row.appendChild(btn('✕ Decline', 'decline', String(inv.leaseId)));
        sc.appendChild(row);
      }

      if (v.receipts.length) {
        head('RECEIPTS');
        for (const r of v.receipts) note(r.text, r.outcome === 'patched' || r.outcome === 'hull' ? 'var(--ax-good)' : 'var(--ax-soft)');
      }
    });
  }
}

export const engineeringPanel = new EngineeringPanel();
(globalThis as unknown as Record<string, unknown>).__engineeringPanel = {
  toggle: () => engineeringPanel.toggle(),
  open: () => engineeringPanel.isOpen(),
  strip: () => document.getElementById('space-eng-strip')?.textContent ?? '',
};
